import { SectionContentPager } from 'app-components/home/sections/components/SectionContentPager';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface SectionSwipeWrapperProps {
  renderPage: (pageIndex: number) => React.ReactNode;
  currentIndex: number;
  totalPages: number;
  onIndexChange: (index: number) => void;
}

export const SectionSwipeWrapper = ({
  renderPage,
  currentIndex,
  totalPages,
  onIndexChange,
}: SectionSwipeWrapperProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState(0);

  const touchStartXRef = useRef<number>(0);
  const touchStartYRef = useRef<number>(0);
  const isAnimatingFromSwipeRef = useRef<boolean>(false);

  useEffect(() => {
    const updateWidth = () => containerRef.current && setContainerWidth(containerRef.current.offsetWidth);
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      if (!innerRef.current || !containerWidth) {
        return;
      }
      const index = page - 1;
      innerRef.current.style.transition = 'none';
      innerRef.current.style.transform = `translateX(${-index * containerWidth}px)`;
      onIndexChange(index);
    },
    [containerWidth, onIndexChange],
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;

    if (innerRef.current) {
      innerRef.current.style.transition = 'none';
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!containerWidth || !innerRef.current) {
        return;
      }

      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;

      const deltaX = currentX - touchStartXRef.current;
      const deltaY = currentY - touchStartYRef.current;

      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        return;
      }

      if (e.cancelable) {
        e.preventDefault();
      }

      const nextX = -currentIndex * containerWidth + deltaX;

      innerRef.current.style.transform = `translateX(${nextX}px)`;
    },
    [currentIndex, containerWidth, totalPages],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!containerWidth || !innerRef.current) {
        return;
      }

      const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
      const threshold = 50;
      const isValidSwipe = Math.abs(deltaX) > threshold;

      const goNext = isValidSwipe && deltaX < 0 && currentIndex < totalPages - 1 ? 1 : 0;
      const goPrev = isValidSwipe && deltaX > 0 && currentIndex > 0 ? 1 : 0;
      const nextIndex = currentIndex + goNext - goPrev;

      isAnimatingFromSwipeRef.current = true;
      innerRef.current.style.transition = 'transform 0.3s ease-out';
      innerRef.current.style.transform = `translateX(${-nextIndex * containerWidth}px)`;

      if (nextIndex !== currentIndex) {
        onIndexChange(nextIndex);
      }
    },
    [currentIndex, containerWidth, totalPages, onIndexChange],
  );

  if (totalPages <= 1) {
    return <div className="w-full">{renderPage(0)}</div>;
  }

  return (
    <div>
      <div ref={containerRef} className="overflow-hidden w-full">
        <div
          ref={innerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="flex"
          style={{
            width: `${totalPages * 100}%`,
            transform: `translateX(${-currentIndex * containerWidth}px)`,
          }}>
          {Array.from({ length: totalPages }).map((_, i) => (
            <div key={i} className="flex-shrink-0" style={{ width: `${100 / totalPages}%` }}>
              {renderPage(i)}
            </div>
          ))}
        </div>
      </div>
      {totalPages > 1 && (
        <SectionContentPager currentPage={currentIndex + 1} totalPages={totalPages} onPageChange={handlePageChange} />
      )}
    </div>
  );
};
